<template>
  <AuthLayout
    sidebar-title="Check your inbox"
    :sidebar-text="sidebarText"
    :support-phone="supportPhone"
  >
    <div class="auth-login__success-icon">
      <AuthIcon name="check" />
    </div>

    <h2 class="auth-login__card-title auth-login__card-title--center" tabindex="-1" data-step-focus>
      Magic link sent
    </h2>

    <p class="auth-login__card-subtitle" style="text-align: center; margin-bottom: 0">
      We sent a secure sign-in link to
      <span class="auth-login__masked">{{ maskedIdentifier }}</span>.
      Click the link to continue signing in. Next, you'll be asked to secure your account with a code sent to your phone.
    </p>

    <div v-if="feedback" class="auth-login__success-banner" role="status">
      <AuthIcon name="check" />
      <span>{{ feedback }}</span>
    </div>

    <span v-if="error" class="auth-login__error-block" role="alert">{{ error }}</span>

    <div class="auth-login__actions">
      <AuthButton
        icon="refresh"
        :loading="loading"
        :disabled="isCoolingDown"
        @click="emit('resend')"
      >
        {{ isCoolingDown ? `Resend in ${countdown}` : 'Resend the link' }}
      </AuthButton>
    </div>

    <p v-if="isCoolingDown" class="auth-login__cooldown-hint">
      Please wait {{ countdown }} before requesting another link.
    </p>

    <button type="button" class="auth-login__back" :disabled="loading" @click="emit('back')">
      ← Back to login options
    </button>
  </AuthLayout>
</template>

<script setup>
import { computed } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthIcon from '../../shared/components/AuthIcon.vue'
import { maskIdentifier } from '../utils/maskIdentifier.js'

const props = defineProps({
  channel:          { type: String, required: true },
  identifier:       { type: String, required: true },
  loading:          { type: Boolean, default: false },
  error:            { type: String,  default: '' },
  feedback:         { type: String,  default: '' },
  isCoolingDown:    { type: Boolean, default: false },
  countdown:        { type: String,  default: '1:00' },
  supportPhone:     { type: String,  default: '815-908-2020' },
  expiresInMinutes: { type: Number, default: 30 },
})

const emit = defineEmits(['resend', 'back'])

const maskedIdentifier = computed(() => maskIdentifier(props.identifier))
const sidebarText = computed(() => {
  const unit = props.expiresInMinutes === 1 ? 'minute' : 'minutes'

  return `The link is valid for ${props.expiresInMinutes} ${unit}. Don't see it? Check your spam folder or click Resend.`
})
</script>
